import { MouseEvent, useCallback, useState } from 'react';
import { Toggle } from '@pwc/pwc-deals-ui';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { Overlay, Popover } from 'react-bootstrap';
import { getIsDarkModeSelector } from 'common/redux/userSettings/userSettingsSlice';
import { isUSTerritoryProject } from 'common/helpers/projectInitUtil';
import {
  defaultColourConversion,
  getContentColourConversion,
  getProjectTerritory,
} from '../project/projectSlice';
import styles from './DefaultTheme.module.scss';

type Props = {
  handleOnToggleClick: () => Promise<void>;
  isPopupVisible: boolean;
};

export const ThemeSwitch = ({ handleOnToggleClick, isPopupVisible }: Props) => {
  const isUserThemeDarkMode = useSelector(getIsDarkModeSelector) as boolean;
  const territory = useSelector(getProjectTerritory);
  const isUSTerritory = isUSTerritoryProject(territory);

  const colourConversion = useSelector(getContentColourConversion);

  const isCustomColourConversion =
    colourConversion.toLowerCase() !== defaultColourConversion.toLowerCase();

  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [showCCToolTip, setShowCCToolTip] = useState(false);
  const { t } = useTranslation('');

  const handleOnContainerClick = useCallback(
    async (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (!isCustomColourConversion) {
        await handleOnToggleClick();
      }
    },
    [handleOnToggleClick, isCustomColourConversion]
  );

  return (
    <>
      {/* eslint-disable-next-line */}
      <div
        className={` ${styles['default-theme--toggle-button']}`}
        data-testid="project-default-theme"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          if (!isCustomColourConversion) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            handleOnContainerClick(e);
          }
        }}
        role="button"
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: '1rem',
            '& label': {
              mb: 0,
            },
          }}
        >
          <Toggle
            checked={isUserThemeDarkMode}
            onClick={(e) => {
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              handleOnContainerClick(e);
            }}
            name="toggle-default-theme"
            disabled={isPopupVisible}
            onMouseEnter={(e: MouseEvent<HTMLElement>) => {
              if (isCustomColourConversion) {
                setTarget(e.currentTarget);
                setShowCCToolTip(true);
              }
            }}
            onMouseLeave={() => {
              if (isCustomColourConversion) {
                setShowCCToolTip(false);
              }
            }}
          />
          <Box
            sx={{
              ml: '0.625rem',
              color: isUserThemeDarkMode ? 'var(--white)' : undefined,
            }}
          >
            {isUserThemeDarkMode ? t('darkMode') : t('lightMode')}
          </Box>
        </Box>
        <Overlay
          target={target}
          show={showCCToolTip}
          placement="bottom"
          rootClose
          onHide={() => {
            setShowCCToolTip(false);
          }}
        >
          <Popover
            id="color-conversion-tooltip"
            data-testid="color-conversion-tooltip"
          >
            <Popover.Content className="popup-user">
              <div className={styles['cc-tooltip-text']}>
                {isUSTerritory
                  ? t('usCustomColorTooltip')
                  : t('customColourTooltip')}
              </div>
            </Popover.Content>
          </Popover>
        </Overlay>
      </div>
    </>
  );
};
